import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getExecutiveDashboardSummary } from "../../api/kpi.api";
import KpiBadge from "../../components/kpi/KpiBadge";
import Button from "../../ui/Button";
import PageState from "../../ui/PageState";
import { colors, radius, spacing, typography } from "../../ui/tokens";
import {
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardStyle,
} from "../../ui/pagePatterns";

const formatDateInput = (value) => value.toISOString().slice(0, 10);

const buildRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return { start, end };
};

export default function ExecutiveKpiDashboard() {
  const navigate = useNavigate();
  const initialRange = useMemo(buildRange, []);
  const [rangeStart, setRangeStart] = useState(formatDateInput(initialRange.start));
  const [rangeEnd, setRangeEnd] = useState(formatDateInput(initialRange.end));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const days = useMemo(() => {
    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 30;
    }
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 1;
  }, [rangeStart, rangeEnd]);

  const loadSummary = () => {
    setLoading(true);
    setError("");
    getExecutiveDashboardSummary({ days })
      .then((res) => setSummary(res.data))
      .catch(() => setError("Unable to load executive KPI summary."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrillThrough = (kpiId) => {
    if (!kpiId) return;
    navigate(`/kpis/${kpiId}`);
  };

  const ragDistribution = summary?.rag_distribution || {};
  const ragPercentages = summary?.rag_percentages || {};

  return (
    <div style={pageShellStyle}>
      <section
        style={{
          ...surfaceCardStyle,
          ...pageHeaderStyle,
          padding: spacing.xl + spacing.sm,
        }}
      >
        <div style={pageHeaderTitleBlockStyle}>
          <h2 style={pageTitleStyle}>Executive KPI Dashboard</h2>
          <p style={pageSubtitleStyle}>
            Review KPI health, red indicators, stale metrics, and performance deterioration across the selected range.
          </p>
        </div>
        <div style={{ ...pageHeaderActionsStyle, alignItems: "center" }}>
          <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Date range</div>
          <input
            type="date"
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
            style={dateInputStyle}
          />
          <span style={{ fontSize: typography.size.xs, color: colors.textMuted }}>to</span>
          <input
            type="date"
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
            style={dateInputStyle}
          />
          <Button onClick={loadSummary} variant="secondary">
            Apply
          </Button>
        </div>
      </section>

      {loading && (
        <PageState
          state="loading"
          align="left"
          title="Loading executive KPI summary"
          description="Glyder is preparing KPI health, trend movement, and stale-metric coverage for leadership review."
        />
      )}
      {error && (
        <PageState
          state="error"
          align="left"
          title="Executive KPI summary unavailable"
          description={`${error} Refresh the dashboard to try again.`}
          action={
            <Button type="button" variant="secondary" onClick={loadSummary}>
              Refresh
            </Button>
          }
        />
      )}

      {!loading && !error && summary && (
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "12px" }}>
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700 }}>KPI Health Overview</div>
                <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{summary.total_kpis} KPIs tracked</div>
              </div>
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Executive view</div>
            </div>
            <div style={{ marginTop: "10px", display: "grid", gap: "6px" }}>
              <HealthRow label="Green" count={ragDistribution.green || 0} percent={ragPercentages.green || 0} color="#16a34a" />
              <HealthRow label="Yellow" count={ragDistribution.yellow || 0} percent={ragPercentages.yellow || 0} color="#f59e0b" />
              <HealthRow label="Red" count={ragDistribution.red || 0} percent={ragPercentages.red || 0} color="#ef4444" />
              <HealthRow label="Stale" count={ragDistribution.stale || 0} percent={0} color="#9ca3af" />
            </div>
            <div style={{ marginTop: "12px" }}>
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted, marginBottom: "6px" }}>Top Red KPIs</div>
              {summary.top_red_kpis?.length ? (
                summary.top_red_kpis.map((item) => (
                  <Button
                    key={item.id}
                    onClick={() => handleDrillThrough(item.id)}
                    variant="secondary"
                    style={listButtonStyle}
                  >
                    <span>{item.name}</span>
                    <KpiBadge value={item.latest_rag} />
                  </Button>
                ))
              ) : (
                <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>No red KPIs in this range.</div>
              )}
            </div>
          </div>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700 }}>Top Risk KPIs</div>
            <div style={{ fontSize: typography.size.xs, color: colors.textMuted, marginBottom: "8px" }}>KPIs linked to key risks</div>
            {summary.top_risk_kpis?.length ? (
              summary.top_risk_kpis.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => handleDrillThrough(item.id)}
                  variant="secondary"
                  style={listButtonStyle}
                >
                  <span>{item.name}</span>
                  <KpiBadge value={item.latest_rag} />
                </Button>
              ))
            ) : (
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>No risk-linked KPIs found for this range.</div>
            )}
          </div>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700 }}>Trending Down KPIs</div>
            <div style={{ fontSize: typography.size.xs, color: colors.textMuted, marginBottom: "8px" }}>KPIs trending worse in the selected range</div>
            {summary.trending_worse?.length ? (
              summary.trending_worse.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => handleDrillThrough(item.id)}
                  variant="secondary"
                  style={listButtonStyle}
                >
                  <div>
                    <div>{item.name}</div>
                    <div style={{ fontSize: "11px", color: colors.textMuted }}>
                      Change: {item.change > 0 ? "+" : ""}{item.change.toFixed(2)}
                    </div>
                  </div>
                  <span style={{ fontSize: typography.size.xs, color: colors.danger }}>Worsening</span>
                </Button>
              ))
            ) : (
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>No KPIs trending down.</div>
            )}
          </div>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700 }}>Stale KPIs</div>
            <div style={{ fontSize: typography.size.xs, color: colors.textMuted, marginBottom: "8px" }}>KPIs overdue for an update</div>
            {summary.stale_kpis?.length ? (
              summary.stale_kpis.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => handleDrillThrough(item.id)}
                  variant="secondary"
                  style={listButtonStyle}
                >
                  <span>{item.name}</span>
                  <KpiBadge value="stale" />
                </Button>
              ))
            ) : (
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>No stale KPIs.</div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function HealthRow({ label, count, percent, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ width: "8px", height: "8px", borderRadius: "999px", background: color }} />
        <span style={{ fontSize: typography.size.xs, color: colors.textSecondary }}>{label}</span>
      </div>
      <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{count} ({percent}%)</div>
    </div>
  );
}

const cardStyle = {
  ...surfaceCardStyle,
  borderRadius: radius.lg,
  padding: spacing.xl + spacing.sm,
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  minHeight: "200px",
};

const listButtonStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px",
  padding: "8px 10px",
  borderRadius: radius.sm,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
  fontSize: typography.size.sm,
  textAlign: "left",
};

const dateInputStyle = {
  padding: "6px 8px",
  borderRadius: radius.sm,
  border: `1px solid ${colors.borderStrong}`,
  background: colors.surface,
  color: colors.textPrimary,
};
